import { useGetMyBuyListTargets, type BuyListQueryParams } from '~/api/buyList';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import type { MyBuyListTargetsQuery } from '~/graphql/graphql';
import type { PaginatedResponse } from '~/types';

export type BuyListTarget =
  MyBuyListTargetsQuery['myBuyLists']['content'][number];

// The server rejects any page size above TimberbaseProperties.maxPageSize (100).
export const MODAL_BUY_LIST_PAGE_SIZE = 50;

export function useMyOpenBuyLists() {
  const {
    data: openBuyLists,
    isLoading,
    hasMoreData,
    pagination,
    error,
    refetch,
    loadMore,
  } = usePaginatedQuery<
    MyBuyListTargetsQuery,
    BuyListTarget,
    BuyListQueryParams
  >({
    fetchHook: useGetMyBuyListTargets,
    select: (data) =>
      data?.myBuyLists as PaginatedResponse<BuyListTarget> | undefined,
    options: {
      pageSize: MODAL_BUY_LIST_PAGE_SIZE,
    },
  });

  const isLoadingFirstPage = isLoading && openBuyLists.length === 0;
  const hasBuyListsFailed = error !== null;
  const areBuyListsReadable =
    !isLoadingFirstPage && (openBuyLists.length > 0 || !hasBuyListsFailed);

  return {
    openBuyLists,
    totalOpenBuyLists: pagination?.totalElements ?? openBuyLists.length,
    isLoadingFirstPage,
    isLoadingMoreBuyLists: isLoading && openBuyLists.length > 0,
    hasBuyListsFailed,
    areBuyListsReadable,
    canLoadMoreBuyLists:
      areBuyListsReadable && !hasBuyListsFailed && hasMoreData !== false,
    loadMoreBuyLists: loadMore,
    retryBuyLists: () => refetch?.(),
  };
}
